import React, { createContext, ReactNode, useEffect, useState } from "react";
import { Buyer, UserData } from "../types/buyerType";
import { Session } from "../types/userTypes";
import { FrontEndFilters } from "../types/frontEndFilterTypes";
import { useMediaQuery } from "@mui/material";
import { defaultAppData, LeadAction } from "../constants/constants.tsx";
import { AdminLeadsCompositeView } from "@common/types/leadsTypes.ts";
import {
    extractCurrentState,
    FilterStatus,
    OriginalLeadState,
    processUpdatedLead
} from "../components/adminComponents/adminLeadsTable/helpers/leadVisualStateHelper.ts";
import { FeatureFlagEnum } from "@common/enums/FeatureFlagEnum.ts";
import { RoleEnum } from "@common/enums/RoleEnum.ts";
import { ADMIN_LEADS_PAGE_SIZE_STORAGE_KEY } from "../reducers/leadsReducer.ts";

type LoginRecord = {
    email: string;
    password: string;
}

export type ModifiedLeadState = {
    leadId: string;
    modifiedButInFilter: boolean;
    isOutOfFilter: boolean;
}
type ModifiedLeadStates = Map<string, ModifiedLeadState>;

export type MainContextType = {
    session: Session | null;
    setSession: React.Dispatch<React.SetStateAction<Session | null>>;
    loadedLeads: AdminLeadsCompositeView[];
    setLoadedLeads: React.Dispatch<React.SetStateAction<AdminLeadsCompositeView[]>>;
    updateLoadedLeads: (updatedLeads: AdminLeadsCompositeView[], keepInDataContext: boolean) => Promise<void>;
    updateLead: (updatedLead: AdminLeadsCompositeView) => void;
    // tracks modified leads in the table
    modifiedLeadStates: ModifiedLeadStates;
    // updates the modified state of one lead
    updateModifiedLeadState: (updatedLeadState: ModifiedLeadState) => void;
    // remember the current state of a lead
    rememberLeadState: (tableLead: AdminLeadsCompositeView) => void;
    // refresh the visual state of a lead with differences from the remembered state
    refreshLeadVisualState: (updatedLead: AdminLeadsCompositeView, currentFilter: FilterStatus, lastAction: LeadAction) => void;
    loadedBuyers: Buyer[];
    setLoadedBuyers: React.Dispatch<React.SetStateAction<Buyer[]>>;
    loggedInUser: UserData | null;
    setLoggedInUser: React.Dispatch<React.SetStateAction<UserData | null>>;
    disputeRate: number;
    setDisputeRate: React.Dispatch<React.SetStateAction<number>>;
    filters: FrontEndFilters;
    setFilters: React.Dispatch<React.SetStateAction<FrontEndFilters>>;
    role: RoleEnum | null;
    setRole: React.Dispatch<React.SetStateAction<RoleEnum | null>>;
    setForgotPasswordEmail: React.Dispatch<React.SetStateAction<string>>;
    forgotPasswordEmail: string;
    loginRecord: LoginRecord;
    setLoginRecord: React.Dispatch<React.SetStateAction<LoginRecord>>;
    onLogout: () => void;
    setDisplayWizard: React.Dispatch<React.SetStateAction<boolean>>;
    displayWizard: boolean;
    setCloseWizard: React.Dispatch<React.SetStateAction<boolean>>;
    closeWizard: boolean;
    buyerDetailSection: number;
    setBuyerDetailSection: React.Dispatch<React.SetStateAction<number>>;
    isScreenMedium: boolean;
    isScreenSmall: boolean;
    featureFlags: FeatureFlagEnum[];
    setFeatureFlags: React.Dispatch<React.SetStateAction<FeatureFlagEnum[]>>;
};

const loadFromLocalStorage = () => {
    try {
        const storedData = localStorage.getItem("appData");
        if (storedData != null) {
            const parsedData = JSON.parse(storedData);
            if (parsedData.version === defaultAppData.version) {
                return parsedData;
            }
            return defaultAppData;
        }
    } catch (error) {
        console.error("Error loading data from local storage:", error);
    }
    return defaultAppData;
};

const MainContext = createContext<MainContextType | undefined>(undefined);

export const DataProvider = ({ children }: { children: ReactNode }) : React.JSX.Element => {
    const initialState = loadFromLocalStorage();
    const [session, setSession] = useState<Session | null>(initialState.session);
    const [loadedLeads, setLoadedLeads] = useState<AdminLeadsCompositeView[]>(defaultAppData.loadedLeads);
    const [loadedBuyers, setLoadedBuyers] = useState<Buyer[]>(initialState.loadedBuyers);
    const [loggedInUser, setLoggedInUser] = useState<UserData | null>(initialState.loggedInUser);
    const [disputeRate, setDisputeRate] = useState<number>(initialState.disputeRate);
    const [filters, setFilters] = useState<FrontEndFilters>(defaultAppData.filters as FrontEndFilters);
    const [role, setRole] = useState<RoleEnum | null>(initialState.role);
    const [forgotPasswordEmail, setForgotPasswordEmail] = useState<string>('');
    const [loginRecord, setLoginRecord] = useState<LoginRecord>({ email: '', password: '' });
    const [displayWizard, setDisplayWizard] = useState<boolean>(false);
    const [closeWizard, setCloseWizard] = useState<boolean>(false);
    const [buyerDetailSection, setBuyerDetailSection] = useState<number>(initialState.buyerDetailSection);
    const [modifiedLeadStates, setModifiedLeadStates] = useState<ModifiedLeadStates>(new Map());
    const [featureFlags, setFeatureFlags] = useState<FeatureFlagEnum[]>(initialState.featureFlags || []);
    const [originalLeadStates, setOriginalLeadStates] = useState<Map<string, OriginalLeadState>>(new Map());
    const isScreenMedium = useMediaQuery('(max-width: 1500px)');
    const isScreenSmall = useMediaQuery('(max-width: 1024px)');

    const updateLoadedLeads = async (updatedLeads: AdminLeadsCompositeView[], keepInDataContext: boolean) => {
        let updatedLoadedLeads;
        if (keepInDataContext) {
            updatedLoadedLeads = loadedLeads.map(lead => {
                const updatedLead = updatedLeads.find(updatedLead => updatedLead.lead.id === lead.lead.id);
                return (updatedLead != null) ? updatedLead : lead;
            });
        } else {
            updatedLoadedLeads = loadedLeads.filter(lead => !updatedLeads.some(updatedLead => updatedLead.lead.id === lead.lead.id));
        }
        setLoadedLeads(updatedLoadedLeads);
    };

    const rememberLeadState = (tableLead: AdminLeadsCompositeView): void => {
        if (originalLeadStates.has(tableLead.lead.id)) {
            return;
        }
        const originalState = extractCurrentState(tableLead);
        setOriginalLeadStates(prev => {
            const newMap = new Map(prev);
            newMap.set(tableLead.lead.id, originalState);
            return newMap;
        });
    }

    const updateLead = (updatedLead: AdminLeadsCompositeView): void => {
        const updatedLeads = loadedLeads.map(lead => {
            if (lead.lead.id === updatedLead.lead.id) {
                return updatedLead;
            }
            return lead;
        });
        setLoadedLeads(updatedLeads);
    };

    const updateModifiedLeadState = (updatedLeadState: ModifiedLeadState): void => {
        setModifiedLeadStates(prev => {
            const newMap = new Map(prev);
            newMap.set(updatedLeadState.leadId, updatedLeadState);
            return newMap;
        });
    };

    const refreshLeadVisualState = (updatedLead: AdminLeadsCompositeView, currentFilter: FilterStatus, lastAction: LeadAction): void => {
        const leadId = updatedLead.lead.id;

        // Replace this with update lead
        const originalState = originalLeadStates.get(leadId);

        if (originalState == null) {
            console.warn(`Original status not found for lead ${leadId}. The visual status will not be updated.`);
            return;
        }

        const newState = processUpdatedLead(updatedLead, originalState, currentFilter, lastAction);
        updateModifiedLeadState(newState);
    };

    function onLogout(): void {
        setLoggedInUser(null);
        setRole(null);
        setSession(null);
        setCloseWizard(false);
        setFilters(defaultAppData.filters as FrontEndFilters);
        setLoadedLeads([]);
        setFeatureFlags([]);
        // Clear persisted buyers filters on logout
        try {
            localStorage.removeItem('adminBuyersFilters');
            localStorage.removeItem(ADMIN_LEADS_PAGE_SIZE_STORAGE_KEY);
        } catch (error) {
            console.error('Error clearing persisted filters on logout:', error);
        }
    }

    useEffect(() => {
        const dataToStore = {
            buyerDetailSection,
            session,
            loadedBuyers,
            loggedInUser,
            role,
            disputeRate,
            featureFlags,
            version: defaultAppData.version,
        };
        try {
            localStorage.setItem("appData", JSON.stringify(dataToStore));
        } catch (error) {
            console.error("Error saving data to local storage:", error);
        }
    }, [buyerDetailSection, session, loadedBuyers, loggedInUser, role, disputeRate, featureFlags]);

    return (
        <MainContext.Provider
            value={{
                session,
                setSession,
                loadedLeads,
                setLoadedLeads,
                updateLoadedLeads,
                updateLead,
                modifiedLeadStates,
                updateModifiedLeadState,
                rememberLeadState,
                refreshLeadVisualState,
                loadedBuyers,
                setLoadedBuyers,
                loggedInUser,
                setLoggedInUser,
                disputeRate,
                setDisputeRate,
                filters,
                setFilters,
                role,
                setRole,
                setForgotPasswordEmail,
                forgotPasswordEmail,
                loginRecord,
                setLoginRecord,
                setDisplayWizard,
                displayWizard,
                setCloseWizard,
                closeWizard,
                buyerDetailSection,
                setBuyerDetailSection,
                onLogout,
                isScreenMedium,
                isScreenSmall,
                featureFlags,
                setFeatureFlags,
            }}
        >
            {children}
        </MainContext.Provider>
    );
};

export default MainContext;
